import React, { useState, useRef, useCallback, useEffect } from "react";
import { API_URL } from "../config";
import "./CharacterDesign.css";

function getToken() {
  return localStorage.getItem("token") || "";
}

/* --- ImageModal --- */
function ImageModal({ src, label, onClose }) {
  if (!src) return null;
  return (
    <div className="cs-modal-overlay" onClick={onClose}>
      <div className="cs-modal" onClick={(e) => e.stopPropagation()}>
        <button className="cs-modal__close" onClick={onClose}>&times;</button>
        <img src={src} alt={label} className="cs-modal__image" />
        <span className="cs-modal__label">{label}</span>
      </div>
    </div>
  );
}

/* --- Inline edit name --- */
function EditableName({ value, onSave }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => { setDraft(value); }, [value]);
  useEffect(() => { if (editing && inputRef.current) inputRef.current.focus(); }, [editing]);

  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft.trim() !== value) onSave(draft.trim());
  };

  if (!editing) {
    return (
      <span className="cd-editable-name" onClick={() => setEditing(true)} title="Haz clic para editar">
        {value} <span className="cd-editable-name__icon">&#9998;</span>
      </span>
    );
  }

  return (
    <input
      ref={inputRef}
      className="cd-editable-name__input"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setDraft(value); setEditing(false); } }}
    />
  );
}

/* --- Main page --- */
export default function CharacterDesign() {
  // Characters list
  const [characters, setCharacters] = useState([]);
  const [selectedChar, setSelectedChar] = useState("");
  const [charDetail, setCharDetail] = useState(null);

  // Create new
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  // Upload
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Generation
  const [status, setStatus] = useState("idle");
  const [generatedViews, setGeneratedViews] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [modalSrc, setModalSrc] = useState(null);
  const pollingRef = useRef(false);

  // Reel refs
  const charsReelRef = useRef(null);
  const refsReelRef = useRef(null);
  const viewsReelRef = useRef(null);
  const scrollReel = (ref, dir) => {
    if (ref.current) ref.current.scrollBy({ left: dir * 200, behavior: "smooth" });
  };

  const headers = { Authorization: `Bearer ${getToken()}` };

  // Readable name from char ID
  const charDisplayName = (id) => {
    const parts = id.replace("char_", "").split("_");
    parts.pop();
    return parts.map((p, i) => i === 0 ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1)).join(" ");
  };

  // Load characters on mount
  const loadCharacters = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/characters`, { headers });
      const d = await res.json();
      const chars = d.characters || [];
      const detailed = await Promise.all(
        chars.map(async (c) => {
          const cid = c.id || c;
          const displayName = c.display_name || "";
          try {
            const res2 = await fetch(`${API_URL}/characters/${cid}`, { headers });
            const detail = await res2.json();
            const faceUrl = detail?.face?.reference?.[0]?.url || null;
            return { id: cid, displayName: detail.display_name || displayName, faceUrl };
          } catch {
            return { id: cid, displayName: displayName, faceUrl: null };
          }
        })
      );
      setCharacters(detailed);
    } catch {}

  }, []);

  useEffect(() => { loadCharacters(); }, [loadCharacters]);

  // Load character detail when selected
  useEffect(() => {
    if (!selectedChar) { setCharDetail(null); return; }
    fetch(`${API_URL}/characters/${selectedChar}`, { headers })
      .then((r) => r.json())
      .then((d) => setCharDetail(d))
      .catch(() => setCharDetail(null));

  }, [selectedChar]);

  // Filter out placeholders
  const filterPlaceholders = (items) =>
    (items || []).filter((i) => !i.name.includes(".emptyFolderPlaceholder"));

  const faceRefs = charDetail ? filterPlaceholders(charDetail.face?.reference) : [];
  const faceViews = charDetail ? filterPlaceholders(charDetail.face?.views) : [];

  // --- Create character ---
  const handleCreate = async () => {
    if (!newName.trim() || creating) return;
    setCreating(true);
    try {
      const form = new FormData();
      form.append("name", newName.trim());
      const res = await fetch(`${API_URL}/characters`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) throw new Error("Error al crear");
      const data = await res.json();
      setNewName("");
      setShowCreate(false);
      await loadCharacters();
      setSelectedChar(data.character_id);
    } catch (err) {
      setErrorMsg(err.message);
    }
    setCreating(false);
  };

  // --- Upload face reference ---
  const handleUploadFace = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedChar) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(`${API_URL}/characters/${selectedChar}/face`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: form,
      });
      if (!res.ok) throw new Error("Error al subir");
      // Reload detail
      const detail = await fetch(`${API_URL}/characters/${selectedChar}`, { headers }).then((r) => r.json());
      setCharDetail(detail);
      // Update thumbnail in list
      setCharacters((prev) =>
        prev.map((c) =>
          c.id === selectedChar
            ? { ...c, faceUrl: detail?.face?.reference?.[0]?.url || c.faceUrl }
            : c
        )
      );
    } catch (err) {
      setErrorMsg(err.message);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // --- Delete face reference ---
  const handleDeleteRef = async (item) => {
    const path = `${selectedChar}/face/reference/${item.name}`;
    try {
      await fetch(`${API_URL}/characters/${selectedChar}/asset?path=${encodeURIComponent(path)}`, {
        method: "DELETE",
        headers,
      });
      const detail = await fetch(`${API_URL}/characters/${selectedChar}`, { headers }).then((r) => r.json());
      setCharDetail(detail);
      setCharacters((prev) =>
        prev.map((c) =>
          c.id === selectedChar
            ? { ...c, faceUrl: detail?.face?.reference?.[0]?.url || null }
            : c
        )
      );
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  // --- Delete character ---
  const handleDeleteChar = async () => {
    if (!selectedChar) return;
    // Delete all assets then the char folder
    try {
      // Get all assets
      const detail = await fetch(`${API_URL}/characters/${selectedChar}`, { headers }).then((r) => r.json());
      const allItems = [
        ...(detail.face?.reference || []).map((i) => `${selectedChar}/face/reference/${i.name}`),
        ...(detail.face?.views || []).map((i) => `${selectedChar}/face/views/${i.name}`),
        ...(detail.body?.reference || []).map((i) => `${selectedChar}/body/reference/${i.name}`),
        ...(detail.body?.views || []).map((i) => `${selectedChar}/body/views/${i.name}`),
        ...(detail.sheets || []).map((i) => `${selectedChar}/sheets/${i.name}`),
      ];
      for (const path of allItems) {
        await fetch(`${API_URL}/characters/${selectedChar}/asset?path=${encodeURIComponent(path)}`, {
          method: "DELETE",
          headers,
        }).catch(() => {});
      }
      setSelectedChar("");
      setCharDetail(null);
      await loadCharacters();
    } catch (err) {
      setErrorMsg(err.message);
    }
  };

  // --- Generate face views ---
  const canGenerate = selectedChar && faceRefs.length > 0 && status !== "generating";

  const pollStatus = useCallback(async (jid) => {
    pollingRef.current = true;
    for (let i = 0; i < 120; i++) {
      if (!pollingRef.current) return;
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const res = await fetch(
          `${API_URL}/character-design/${encodeURIComponent(jid)}/status`,
          { headers: { Authorization: `Bearer ${getToken()}` } }
        );
        const data = await res.json();
        if (data.status === "completed") {
          const resultRes = await fetch(
            `${API_URL}/character-design/${encodeURIComponent(jid)}/result`,
            { headers: { Authorization: `Bearer ${getToken()}` } }
          );
          const result = await resultRes.json();
          setGeneratedViews(result.image || null);
          setStatus("done");
          pollingRef.current = false;
          // Reload detail to get saved views
          const detail = await fetch(`${API_URL}/characters/${selectedChar}`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          }).then((r) => r.json());
          setCharDetail(detail);
          return;
        }
        if (data.status === "error") {
          setErrorMsg(data.error || "Error al generar");
          setStatus("error");
          pollingRef.current = false;
          return;
        }
      } catch (err) {
        console.error("Poll error:", err);
      }
    }
    setErrorMsg("Tiempo agotado");
    setStatus("error");
    pollingRef.current = false;

  }, [selectedChar]);

  const handleGenerate = async () => {
    setStatus("generating");
    setGeneratedViews(null);
    setErrorMsg("");
    try {
      const res = await fetch(`${API_URL}/character-design/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ character_id: selectedChar }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || `Error ${res.status}`);
      }
      const data = await res.json();
      pollStatus(data.job_id);
    } catch (err) {
      setErrorMsg(err.message || "Error al enviar");
      setStatus("error");
    }
  };

  // --- Save generated views ---
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!generatedViews || !selectedChar) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/character-design/save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ character_id: selectedChar, image_b64: generatedViews }),
      });
      if (res.ok) {
        const detail = await fetch(`${API_URL}/characters/${selectedChar}`, { headers }).then((r) => r.json());
        setCharDetail(detail);
      }
    } catch {}
    setSaving(false);
  };

  // --- Split into 4 individual reference photos ---
  const [splitting, setSplitting] = useState(false);
  const handleSplit = async () => {
    if (!generatedViews || !selectedChar) return;
    setSplitting(true);
    try {
      const res = await fetch(`${API_URL}/character-design/split`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ character_id: selectedChar, image_b64: generatedViews }),
      });
      if (res.ok) {
        const detail = await fetch(`${API_URL}/characters/${selectedChar}`, { headers }).then((r) => r.json());
        setCharDetail(detail);
      }
    } catch {}
    setSplitting(false);
  };

  // --- Load a saved view into the result panel ---
  const loadViewToResult = async (url) => {
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const b64 = reader.result.split(",")[1];
        setGeneratedViews(b64);
        setStatus("done");
      };
      reader.readAsDataURL(blob);
    } catch {}
  };

  const downloadImage = (b64) => {
    const link = document.createElement("a");
    link.href = b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
    link.download = `face_views_${selectedChar}.png`;
    link.click();
  };

  const getB64Src = (b64) => {
    if (!b64) return null;
    return b64.startsWith("data:") ? b64 : `data:image/png;base64,${b64}`;
  };

  return (
    <div className="cd-page">
      {/* LEFT — Character management */}
      <div className="cs-panel cs-panel--left cd-panel--left">
        <div className="cs-result-header">
          <h2 className="cs-panel__title">Crea Personaje</h2>
          <div className="cd-header-actions">
            <button
              className="cd-btn-new"
              onClick={() => setShowCreate(!showCreate)}
            >
              {showCreate ? "Cancelar" : "+ Nuevo"}
            </button>
            <button
              className={`cs-btn-generate ${status === "generating" ? "cs-btn-generate--loading" : ""}`}
              disabled={!canGenerate}
              onClick={handleGenerate}
            >
              {status === "generating" ? "Generando..." : "Generar Vistas"}
            </button>
          </div>
        </div>

        {/* Create new character */}
        {showCreate && (
          <div className="cd-create-bar">
            <input
              className="cd-create-bar__input"
              type="text"
              placeholder="Nombre del personaje..."
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
            />
            <button
              className="cd-create-bar__btn"
              disabled={!newName.trim() || creating}
              onClick={handleCreate}
            >
              {creating ? "Creando..." : "Crear"}
            </button>
          </div>
        )}

        {/* Character selector — reel */}
        <section className="cs-section">
          <span className="cs-section__title">Personajes</span>
          <div className="cs-reel">
            <button className="cs-reel__btn" onClick={() => scrollReel(charsReelRef, -1)}>{"\u25C0"}</button>
            <div className="cs-reel__frames" ref={charsReelRef}>
              {characters.map((c) => (
                <div
                  key={c.id}
                  className={`cs-reel-item ${selectedChar === c.id ? "cs-reel-item--selected" : ""}`}
                  onClick={() => { setSelectedChar(c.id); setGeneratedViews(null); setStatus("idle"); setErrorMsg(""); }}
                >
                  {c.faceUrl ? (
                    <img src={c.faceUrl} alt={c.id} />
                  ) : (
                    <div className="cs-reel-item__placeholder" />
                  )}
                  <span className="cs-reel-item__name">{c.displayName || charDisplayName(c.id)}</span>
                </div>
              ))}
              {characters.length === 0 && (
                <span className="cs-reel-empty">No hay personajes. Crea uno nuevo.</span>
              )}
            </div>
            <button className="cs-reel__btn" onClick={() => scrollReel(charsReelRef, 1)}>{"\u25B6"}</button>
          </div>
        </section>

        {/* Selected character detail */}
        {selectedChar && charDetail && (
          <>
            {/* Character info bar */}
            <div className="cd-char-info">
              <EditableName
                value={characters.find((c) => c.id === selectedChar)?.displayName || charDisplayName(selectedChar)}
                onSave={async (newName) => {
                  try {
                    await fetch(`${API_URL}/characters/${selectedChar}`, {
                      method: "PATCH",
                      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
                      body: JSON.stringify({ display_name: newName }),
                    });
                    setCharacters((prev) => prev.map((c) => c.id === selectedChar ? { ...c, displayName: newName } : c));
                  } catch (err) {
                    setErrorMsg(err.message);
                  }
                }}
              />
              <button className="cd-btn-delete" onClick={handleDeleteChar} title="Eliminar personaje">
                &#x1F5D1;
              </button>
            </div>

            {/* Face references */}
            <section className="cs-section">
              <div className="cd-section-header">
                <span className="cs-section__title">Fotos de referencia</span>
                <button
                  className="cd-btn-upload"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? "Subiendo..." : "+ Subir foto"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleUploadFace}
                />
              </div>
              <div className="cs-reel cs-reel--tall">
                <button className="cs-reel__btn" onClick={() => scrollReel(refsReelRef, -1)}>{"\u25C0"}</button>
                <div className="cs-reel__frames" ref={refsReelRef}>
                  {faceRefs.length > 0 ? (
                    faceRefs.map((item) => (
                      <div key={item.name} className="cs-reel-item cs-reel-item--wide cd-ref-item">
                        <img src={item.url} alt={item.name} onClick={() => setModalSrc(item.url)} />
                        <button className="cd-ref-item__delete" onClick={() => handleDeleteRef(item)} title="Eliminar">&times;</button>
                        <span className="cs-reel-item__name">{item.name.replace(".png", "").replace(/_/g, " ")}</span>
                      </div>
                    ))
                  ) : (
                    <span className="cs-reel-empty">Sube al menos una foto de referencia</span>
                  )}
                </div>
                <button className="cs-reel__btn" onClick={() => scrollReel(refsReelRef, 1)}>{"\u25B6"}</button>
              </div>
            </section>

            {/* Saved face views */}
            <section className="cs-section">
              <span className="cs-section__title">Vistas generadas</span>
              <div className="cs-reel">
                <button className="cs-reel__btn" onClick={() => scrollReel(viewsReelRef, -1)}>{"\u25C0"}</button>
                <div className="cs-reel__frames" ref={viewsReelRef}>
                  {faceViews.length > 0 ? (
                    faceViews.map((item) => (
                      <div key={item.name} className="cs-reel-item cs-reel-item--sheet" onClick={() => loadViewToResult(item.url)} title="Cargar en resultado">
                        <img src={item.url} alt={item.name} />
                      </div>
                    ))
                  ) : (
                    <span className="cs-reel-empty">Genera vistas del personaje</span>
                  )}
                </div>
                <button className="cs-reel__btn" onClick={() => scrollReel(viewsReelRef, 1)}>{"\u25B6"}</button>
              </div>
            </section>
          </>
        )}

        {errorMsg && <div className="cs-error">{errorMsg}</div>}
      </div>

      {/* RIGHT — Result */}
      <div className="cs-panel cs-panel--right cd-panel--right">
        <h2 className="cs-panel__title">Resultado</h2>

        {status === "idle" && !generatedViews && (
          <div className="cs-results-empty">
            <p>Selecciona un personaje con foto de referencia y pulsa Generar Vistas</p>
          </div>
        )}

        <div className="cs-results-grid">
          <div className="cs-result-card">
            <div
              className={`cs-result-card__image ${generatedViews ? "cs-result-card__image--loaded" : ""}`}
              onClick={() => generatedViews && setModalSrc(getB64Src(generatedViews))}
            >
              {generatedViews ? (
                <img src={getB64Src(generatedViews)} alt="Face views" />
              ) : (
                <div className="cs-result-card__placeholder">
                  {status === "generating" && <span className="cs-spinner" />}
                </div>
              )}
            </div>
            <div className="cs-result-card__footer">
              <span className="cs-result-card__label">Vistas del rostro</span>
              <div className="cs-result-card__actions">
                {generatedViews && (
                  <button
                    className="cs-btn-save"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? "Guardando..." : "Guardar"}
                  </button>
                )}
                {generatedViews && (
                  <button
                    className="cd-btn-split"
                    onClick={handleSplit}
                    disabled={splitting}
                    title="Cortar en 4 vistas individuales y guardar como referencia"
                  >
                    {splitting ? "Cortando..." : "Split"}
                  </button>
                )}
                {generatedViews && (
                  <button
                    className="cs-result-card__download"
                    onClick={() => downloadImage(generatedViews)}
                  >
                    &#x2B73;
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {modalSrc && (
        <ImageModal
          src={modalSrc}
          label="Vista"
          onClose={() => setModalSrc(null)}
        />
      )}
    </div>
  );
}
